import { Link } from 'react-router-dom'
import { APIBASE } from '../../../constants/api.constants'
import { APIROUTES, fillPath } from '../../../constants/routes.constants'
import type { ShopProductCardDto } from '../../../viewmodels/useShopProducts'

/**
 * ShopProductCard — one tile in the /shop list. Thumbnail + name +
 * price (range when the variants span multiple prices) + stock chip.
 * Clicks navigate to the detail page (/shop/:id).
 *
 * Pure presentational.
 */
function rupees(paisa: number): string {
  return `₹${(paisa / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
}

function priceLabel(min: number | null, max: number | null): string {
  if (min === null && max === null) return '—'
  if (min === max || min === null || max === null) return rupees((min ?? max) as number)
  return `${rupees(min)} – ${rupees(max)}`
}

export function ShopProductCard({ product }: { readonly product: ShopProductCardDto }): JSX.Element {
  const detailHref = `/shop/${encodeURIComponent(product.id)}`
  // ?thumb=1 asks /shop/media/:id for the 800px WebP variant — the
  // worker generates + caches it in R2 the first time a thumbnail is
  // selected; subsequent reads stream from R2 directly. Original is
  // still served without the query param for the detail-page gallery.
  const thumbnailUrl =
    product.thumbnail_media_id === null
      ? null
      : `${APIBASE}${fillPath(APIROUTES.SHOPMEDIA, { id: product.thumbnail_media_id })}?thumb=1`
  const inStock = product.total_on_hand > 0
  return (
    <Link
      to={detailHref}
      // Cinematic hover lift (Kiran 2026-06-08): the whole tile rises +
      // warms the border + scales the photo. Slow EASE matches the shared
      // motion kit so it feels of-a-piece with reveals across the site.
      className="group flex flex-col overflow-hidden rounded-2xl border border-border/40 bg-bg-primary/72 shadow-sm backdrop-blur-md transition-all duration-500 ease-cinematic hover:-translate-y-1 hover:shadow-xl hover:border-forest/60"
    >
      <div className="aspect-square w-full overflow-hidden bg-parchment/40">
        {thumbnailUrl !== null ? (
          <img
            src={thumbnailUrl}
            alt={product.name}
            loading="lazy"
            decoding="async"
            // Explicit dimensions help the browser allocate space + fetch
            // the bytes sooner; the variant is 800px so width=800 matches
            // the worker's output. CSS still constrains to the card width.
            width={800}
            height={800}
            className="h-full w-full object-cover transition-transform duration-700 ease-cinematic group-hover:scale-[1.06]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center font-body text-body-sm text-text-secondary">
            No image
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <p className="font-body text-body-xs text-text-secondary">{product.category_name}</p>
        <h3 className="font-heading text-h4 text-charcoal leading-tight line-clamp-2">{product.name}</h3>
        <div className="mt-auto flex items-center justify-between gap-2 pt-2">
          <span className="font-mono text-body text-charcoal">
            {priceLabel(product.min_price_paisa, product.max_price_paisa)}
          </span>
          {inStock ? null : (
            <span className="font-body text-body-xs text-terra">Out of stock</span>
          )}
        </div>
      </div>
    </Link>
  )
}
